import type { SelectionAction } from "~/features/documents/editor/selectionTypes";
import { noteFrom, useNudgePicker } from "~/features/nudges/useNudgePicker";
import { useTaskizeTray } from "~/features/taskize/useTaskizeTray";

/**
 * The two *Do* entries in the ✨ selection menu.
 *
 * They are declared client-side, not fetched, because they are not selection *transforms*: they
 * act on the workspace (make records, set a reminder) rather than rewriting the text, and no
 * catalogue row could configure them. kms declared the same two as `staticAIActions`; W6's
 * `DO_ACTION_IDS` already expects exactly these ids, so the menu groups them for free.
 *
 * `ask` is deliberately absent: it is catalogued in kms, declared in three places and
 * implemented in none (D6). The Ask bar and ⌘K are where a question goes.
 */
export const SELECTION_DO_ACTIONS: SelectionAction[] = [
  {
    id: "taskize",
    title: "Taskize",
    icon: "list-checks",
    prompt: "",
    shortcut: null,
    showInMenu: true,
    sortOrder: 90,
    scope: "System",
    isBuiltIn: true,
  },
  {
    id: "nudge",
    title: "Nudge me…",
    icon: "bell",
    prompt: "",
    shortcut: null,
    showInMenu: true,
    sortOrder: 91,
    scope: "System",
    isBuiltIn: true,
  },
];

export function isDoAction(actionId: string): boolean {
  return SELECTION_DO_ACTIONS.some((action) => action.id === actionId);
}

/**
 * Run one. The selection travels with it: Taskize scopes the extraction to what is highlighted,
 * and the nudge picker anchors to the selection's own rect and quotes its first 60 characters.
 */
export function runDoAction(actionId: string, documentId: string, selectedText: string): boolean {
  if (actionId === "taskize") {
    useTaskizeTray.getState().openTray(documentId, selectedText.trim() || undefined);
    return true;
  }
  if (actionId === "nudge") {
    // A rect is a nicety, not a requirement: the picker centres itself without one, and
    // `getBoundingClientRect` is absent on jsdom's Range and on a detached one anywhere.
    let rect: DOMRect | null = null;
    try {
      const selection = typeof window === "undefined" ? null : window.getSelection();
      const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      rect =
        typeof range?.getBoundingClientRect === "function" ? range.getBoundingClientRect() : null;
    } catch {
      rect = null;
    }
    useNudgePicker.getState().openPicker({
      target: { targetType: "document", documentId },
      anchorRect: rect,
      defaultNote: noteFrom(selectedText),
    });
    return true;
  }
  return false;
}
